import { useCallback, useEffect, useRef, useState } from "react";
import { withClientContext } from "~/apollo/client";
import {
  JOB_POLLING_INTERVAL,
  STATUS_COMPLETED,
  STATUS_FAILED,
} from "~/common/constants/asyncJob";
import { useGetAsyncJobQuery } from "~/graphql/mainHooks";
import { CustomReportRecipientObjectType } from "~/graphql/supergraphEnums";
import { useGenerateCustomReportTemplatePreviewPdfMutation } from "~/graphql/supergraphHooks";

/**
 * The async job's `data` is an untyped JSON blob (a JSONString scalar). The backend
 * stashes the short-lived presigned preview URL there on completion; pull it out,
 * tolerating either a raw JSON string or an already-parsed object.
 */
const readPreviewDownloadUrl = (raw: unknown): string => {
  if (!raw) return "";
  try {
    const parsed: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed && typeof parsed === "object") {
      const url = (parsed as Record<string, unknown>).preview_download_url;
      return typeof url === "string" ? url : "";
    }
  } catch {
    // Malformed JSON — treat as no URL available.
  }
  return "";
};

type UseTemplatePreviewJobParams = {
  /** Persisted-template path: the published template + version to render. */
  reportTemplateId?: number | null;
  reportTemplateVersionId?: number | null;
  /**
   * Draft-template path: an in-progress docx uploaded to the preview prefix plus
   * the user-selected query. Mutually exclusive with reportTemplateId/version.
   */
  templateS3Key?: string | null;
  queryId?: number | null;
  recipientObjectType: CustomReportRecipientObjectType;
  recipientObjectId: number | null;
  /** Federation global ID of the operating arena; enables the cross-arena (canopy) path. */
  arenaGlobalId: string;
  /**
   * Concrete values for the date parameters the query requires, keyed by the
   * snake_case token the semantic layer resolves against. Reaches it as
   * `query.params` by way of the preview workflow's `recipient_metadata`.
   */
  parameters?: Record<string, string> | null;
  skip?: boolean;
  /**
   * GL transaction IDs for transaction-based report previews. When provided, serialized
   * as the `gl_transaction_ids` runtime input and forwarded to the backend via the
   * `parameters` mutation argument.
   */
  glTransactionIds?: string[] | null;
};

/**
 * Drives template preview generation through the unified Temporal workflow:
 * fire the GenerateCustomReportTemplatePreviewPdf mutation, poll the resulting
 * AsyncJob, and expose the served preview PDF URL once the job completes.
 *
 * Supports two sources: a persisted template (reportTemplateId + version) as the
 * viewer uses, or a draft docx (templateS3Key + queryId) as the editor's
 * preview-and-publish step uses before the template is created.
 *
 * Debounced by 500ms so rapid recipient/version/edit changes don't spam the worker.
 */
export const useTemplatePreviewJob = ({
  reportTemplateId,
  reportTemplateVersionId,
  templateS3Key,
  queryId,
  recipientObjectType,
  recipientObjectId,
  arenaGlobalId,
  parameters,
  skip = false,
  glTransactionIds,
}: UseTemplatePreviewJobParams) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [jobId, setJobId] = useState("");

  const refresh = useCallback(() => setRetryCount((c) => c + 1), []);

  const renderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard against acting twice on the same job's terminal result.
  const finishedRef = useRef(false);
  // Monotonic "latest request wins" guard so an out-of-order mutation can't set a stale jobId.
  const requestIdRef = useRef(0);

  // The mutation takes a JSONString, and serializing here also gives the render
  // effect a primitive to depend on — a caller that rebuilds the parameters
  // object every render can't retrigger the preview on its own. Date parameters
  // and the transaction-based runtime input are merged into one parameters payload.
  const hasParameters = Boolean(parameters && Object.keys(parameters).length > 0);
  const hasGlTransactions = Boolean(glTransactionIds?.length);
  const serializedParameters =
    hasParameters || hasGlTransactions
      ? JSON.stringify({
          ...(hasParameters ? parameters : {}),
          ...(hasGlTransactions ? { gl_transaction_ids: glTransactionIds } : {}),
        })
      : undefined;

  const [generatePreview] = useGenerateCustomReportTemplatePreviewPdfMutation({
    fetchPolicy: "no-cache",
  });

  const {
    data: jobData,
    error: jobError,
    startPolling,
    stopPolling,
  } = useGetAsyncJobQuery({
    variables: { jobId, arenaId: arenaGlobalId || null },
    skip: !jobId,
    context: withClientContext("staff"),
    fetchPolicy: "network-only",
  });

  // Poll the current job via startPolling/stopPolling (the shared async-job pattern).
  useEffect(() => {
    if (!jobId) {
      return undefined;
    }
    startPolling(JOB_POLLING_INTERVAL);
    return () => stopPolling();
  }, [jobId, startPolling, stopPolling]);

  // React to poll results. Ignore data that belongs to a superseded job (Apollo can
  // surface the previous job's result while the new query is in flight).
  useEffect(() => {
    const asyncJob = jobData?.asyncJob;
    if (finishedRef.current || !jobId || asyncJob?.id !== jobId) {
      return;
    }
    if (asyncJob.status === STATUS_COMPLETED) {
      finishedRef.current = true;
      stopPolling();
      // The backend stashes a short-lived presigned S3 GET URL in the job data,
      // so the PDF is fetched straight from S3 with no endpoint to authorize.
      const downloadUrl = readPreviewDownloadUrl(asyncJob.data);
      if (!downloadUrl) {
        setRenderError("Preview response was empty");
        setIsRendering(false);
        return;
      }
      setPdfUrl(downloadUrl);
      setIsRendering(false);
    } else if (asyncJob.status === STATUS_FAILED) {
      finishedRef.current = true;
      stopPolling();
      setRenderError("Failed to generate preview");
      setIsRendering(false);
    }
  }, [jobData, jobId, stopPolling]);

  // React to poll transport errors.
  useEffect(() => {
    if (finishedRef.current || !jobId || !jobError) {
      return;
    }
    finishedRef.current = true;
    stopPolling();
    setRenderError("Failed to generate preview");
    setIsRendering(false);
  }, [jobError, jobId, stopPolling]);

  useEffect(() => {
    const cleanup = () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
        renderTimeoutRef.current = null;
      }
    };

    const isDraft = Boolean(templateS3Key && queryId);
    const hasPersistedSource = Boolean(reportTemplateId && reportTemplateVersionId);
    const hasSource = isDraft || hasPersistedSource;

    if (!hasSource || !recipientObjectId || skip) {
      cleanup();
      requestIdRef.current += 1;
      setPdfUrl(null);
      setIsRendering(false);
      setRenderError(null);
      setJobId("");
      finishedRef.current = false;
      return cleanup;
    }

    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    renderTimeoutRef.current = setTimeout(() => {
      const requestId = (requestIdRef.current += 1);

      setIsRendering(true);
      setRenderError(null);
      setPdfUrl(null);
      setJobId("");
      finishedRef.current = false;

      generatePreview({
        variables: isDraft
          ? {
              templateS3Key,
              queryId,
              recipientObjectType,
              recipientObjectId,
              arenaId: arenaGlobalId || undefined,
              parameters: serializedParameters,
            }
          : {
              reportTemplateId,
              reportTemplateVersionId,
              recipientObjectType,
              recipientObjectId,
              arenaId: arenaGlobalId || undefined,
              parameters: serializedParameters,
            },
      })
        .then((result) => {
          if (requestIdRef.current !== requestId) {
            return;
          }
          const newJobId = result.data?.generateCustomReportTemplatePreviewPdf?.job?.id ?? "";
          if (!newJobId) {
            setRenderError("Failed to start preview generation");
            setIsRendering(false);
            return;
          }
          finishedRef.current = false;
          setJobId(newJobId);
        })
        .catch(() => {
          if (requestIdRef.current !== requestId) {
            return;
          }
          setRenderError("Failed to start preview generation");
          setIsRendering(false);
        });
    }, 500);

    return cleanup;
  }, [
    reportTemplateId,
    reportTemplateVersionId,
    templateS3Key,
    queryId,
    recipientObjectType,
    recipientObjectId,
    arenaGlobalId,
    serializedParameters,
    skip,
    generatePreview,
    retryCount,
  ]);

  return { pdfUrl, isRendering, renderError, refresh };
};
